/** Auth composable — Vue side. Manages the signed-in user state across the SPA. */

import { ref, computed } from 'vue';

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export interface AuthUser {
  id: number;
  email: string;
  role: 'customer' | 'manager' | 'admin' | 'super-admin';
  name?: string | null;
}

const user = ref<AuthUser | null>(null);
const loading = ref(true);
let initPromise: Promise<void> | null = null;

async function fetchMe(): Promise<void> {
  loading.value = true;
  try {
    const res = await fetch(`${BASE}/api/auth/me`, { credentials: 'include' });
    if (res.ok) {
      const j = await res.json() as { user: AuthUser };
      user.value = j.user;
    } else {
      user.value = null;
    }
  } catch {
    user.value = null;
  } finally {
    loading.value = false;
  }
}

export function useAuth() {
  if (!initPromise) initPromise = fetchMe();

  async function requestOtp(email: string): Promise<{ ok: boolean; email_masked?: string; error?: string; reset_in?: number }> {
    const res = await fetch(`${BASE}/api/auth/request-otp`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return res.json();
  }

  async function verifyOtp(email: string, code: string): Promise<{ ok: boolean; user?: AuthUser; error?: string; attempts_left?: number }> {
    const res = await fetch(`${BASE}/api/auth/verify-otp`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const j = await res.json();
    if (j.ok && j.user) user.value = j.user;
    return j;
  }

  async function logout(): Promise<void> {
    await fetch(`${BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    user.value = null;
  }

  return {
    user,
    loading,
    isAuthenticated: computed(() => user.value !== null),
    isAdmin: computed(() => user.value !== null && ['admin', 'super-admin', 'manager'].includes(user.value.role)),
    requestOtp,
    verifyOtp,
    logout,
    refresh: fetchMe,
  };
}
